import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Tenant } from '@/shared/types';
import { APP_CONFIG } from '@/shared/config/config';
import { sessionService } from '@/shared/services/session.service';

// Async thunks
export const fetchTenantAsync = createAsyncThunk(
  'tenant/fetchTenant',
  async (subdomain: string) => {
    // TODO: Implementar llamada real a la API
    const response = await fetch(
      `${APP_CONFIG.api.baseUrl}/tenants/${subdomain}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch tenant');
    }

    const data = await response.json();
    return data;
  }
);

export const updateTenantAsync = createAsyncThunk(
  'tenant/updateTenant',
  async (tenantData: Partial<Tenant>) => {
    // TODO: Implementar llamada real a la API
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const user = sessionService.getUser();
    if (user?.tenantId) {
      headers['x-tenant-id'] = user.tenantId;
    }

    const response = await fetch(
      `${APP_CONFIG.api.baseUrl}/tenants/${tenantData.id}`,
      {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(tenantData),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update tenant');
    }

    const data = await response.json();
    return data;
  }
);

// State interface
interface TenantState {
  currentTenant: Tenant | null;
  subdomain: string | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: TenantState = {
  currentTenant: null,
  subdomain: null,
  loading: false,
  error: null,
};

// Slice
const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setTenant: (state, action: PayloadAction<Tenant>) => {
      state.currentTenant = action.payload;
      state.subdomain = action.payload.subdomain;
    },
    setSubdomain: (state, action: PayloadAction<string>) => {
      state.subdomain = action.payload;
    },
    clearTenant: state => {
      state.currentTenant = null;
      state.subdomain = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch tenant
    builder
      .addCase(fetchTenantAsync.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTenant = action.payload;
        state.subdomain = action.payload.subdomain;
        state.error = null;
      })
      .addCase(fetchTenantAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tenant';
      });

    // Update tenant
    builder
      .addCase(updateTenantAsync.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTenantAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTenant = action.payload;
        state.error = null;
      })
      .addCase(updateTenantAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update tenant';
      });
  },
});

export const { setTenant, setSubdomain, clearTenant, setError, clearError } =
  tenantSlice.actions;

export default tenantSlice.reducer;

// Export types
export type { Tenant } from '@/shared/types';
